import { useRef, useState, useEffect, useCallback } from 'react';
import { useThemeColors } from '@/styles';
import { typography } from '@/styles/design-tokens';
import {
  useLoginTransitionStore,
  setTransitionPhase,
  resetTransition,
  getTransitionState,
} from '@/stores/loginTransitionStore';
import { PulsatingLogo } from './PulsatingLogo';

const LOADING_MESSAGES = [
  'Sharpening your pencils...',
  'Opening your notebook...',
  'Finding the best lesson...',
  'Almost there...',
  'Loading your learning path...',
  'Setting up your desk...',
  'Preparing your board...',
  'Getting your cards ready...',
];

const LOGO_SIZE = 192; // matches login screen h-48
const MESSAGE_ROTATE_MS = 2500;
const MIN_DISPLAY_MS = 3000;
const HEADER_WAIT_TIMEOUT_MS = 3000;
const FLIGHT_DURATION_MS = 1400;
const PARTICLE_COUNT = 10;

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
}

interface LogoTransitionOverlayProps {
  onFinish: () => void;
}

export function LogoTransitionOverlay({
  onFinish,
}: LogoTransitionOverlayProps) {
  const colors = useThemeColors();
  const store = useLoginTransitionStore();

  // Stable ref for onFinish — prevents effect cascades when parent re-renders
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const dismissing = useRef(false);
  const minTimeElapsed = useRef(false);
  const minTimerStartRef = useRef(0);
  const headerWaitTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseInitialized = useRef(false);
  const logoElRef = useRef<HTMLDivElement>(null);

  // Logo animation state
  const [logoStyle, setLogoStyle] = useState<React.CSSProperties>({});
  const [overlayOpacity, setOverlayOpacity] = useState(1); // Start at 1 — instant cover
  const [messageOpacity, setMessageOpacity] = useState(0);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [burstParticles, setBurstParticles] = useState<Particle[]>([]);

  // Rotating messages
  const [messageIndex, setMessageIndex] = useState(() =>
    Math.floor(Math.random() * LOADING_MESSAGES.length)
  );
  const [textOpacity, setTextOpacity] = useState(1);

  // Message rotation
  useEffect(() => {
    if (!store.visible) return;
    const interval = setInterval(() => {
      setTextOpacity(0);
      setTimeout(() => {
        setMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
        setTextOpacity(1);
      }, 200);
    }, MESSAGE_ROTATE_MS);
    return () => clearInterval(interval);
  }, [store.visible]);

  // Dismiss overlay (error path)
  const dismiss = useCallback(() => {
    if (dismissing.current) return;
    dismissing.current = true;
    setOverlayOpacity(0);
    setTimeout(() => {
      resetTransition();
      onFinishRef.current();
    }, 400);
  }, []);

  // Generate particles along an arc path
  const generateParticles = useCallback(
    (startX: number, startY: number, endX: number, endY: number) => {
      const newParticles: Particle[] = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const t = i / PARTICLE_COUNT;
        const arcHeight = -120 * Math.sin(t * Math.PI);
        const x = startX + (endX - startX) * t + (Math.random() - 0.5) * 40;
        const y =
          startY + (endY - startY) * t + arcHeight + (Math.random() - 0.5) * 30;
        newParticles.push({
          id: i,
          x,
          y,
          size: 4 + Math.random() * 6,
          delay: t * FLIGHT_DURATION_MS * 0.6,
        });
      }
      setParticles(newParticles);
    },
    []
  );

  // Animate logo from center to header — the fun part!
  const startHeaderTransition = useCallback(() => {
    const { phase, headerLogoRect } = getTransitionState();
    if (!headerLogoRect || phase !== 'pulsating') return;

    // Fade out messages
    setMessageOpacity(0);
    setTransitionPhase('center-to-header');

    const centerX = (window.innerWidth - LOGO_SIZE) / 2;
    const centerY = (window.innerHeight - LOGO_SIZE) / 2;
    const targetScale = headerLogoRect.height / LOGO_SIZE;

    // Target: center of the header logo
    const targetX =
      headerLogoRect.x +
      headerLogoRect.width / 2 -
      (LOGO_SIZE * targetScale) / 2;
    const targetY =
      headerLogoRect.y +
      headerLogoRect.height / 2 -
      (LOGO_SIZE * targetScale) / 2;

    // Offsets from center (the element starts at centerX/centerY)
    const dx = targetX - centerX;
    const dy = targetY - centerY;

    // Arc midpoint — rises above the straight line
    const midDx = dx / 2;
    const midDy = Math.min(dy / 2, dy - 120);
    const midScale = (1 + targetScale) / 2;

    // Spawn trail particles along the arc
    generateParticles(
      centerX + LOGO_SIZE / 2,
      centerY + LOGO_SIZE / 2,
      headerLogoRect.x + headerLogoRect.width / 2,
      headerLogoRect.y + headerLogoRect.height / 2
    );

    // Use Web Animations API for the arcing flight with spin
    const el = logoElRef.current;
    if (el) {
      el.animate(
        [
          {
            transform: 'translate(0, 0) scale(1) rotate(0deg)',
            offset: 0,
          },
          {
            // Squish before launch
            transform: 'translate(0, 8px) scale(1.08, 0.92) rotate(0deg)',
            offset: 0.07,
          },
          {
            // Arc peak — spinning
            transform: `translate(${midDx}px, ${midDy}px) scale(${midScale + 0.05}) rotate(180deg)`,
            offset: 0.45,
          },
          {
            // Approaching header
            transform: `translate(${dx}px, ${dy - 8}px) scale(${targetScale * 1.25}) rotate(340deg)`,
            offset: 0.78,
          },
          {
            // Overshoot bounce
            transform: `translate(${dx}px, ${dy + 3}px) scale(${targetScale * 0.88}) rotate(360deg)`,
            offset: 0.9,
          },
          {
            // Settle into place
            transform: `translate(${dx}px, ${dy}px) scale(${targetScale}) rotate(360deg)`,
            offset: 1,
          },
        ],
        {
          duration: FLIGHT_DURATION_MS,
          easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fill: 'forwards',
        }
      );
    }

    // Fade out overlay background partway through the flight
    setTimeout(() => {
      setOverlayOpacity(0);
    }, FLIGHT_DURATION_MS * 0.35);

    // Landing burst — small circles radiating outward
    setTimeout(() => {
      const burst: Particle[] = Array.from({ length: 8 }).map((_, i) => ({
        id: i,
        x: headerLogoRect.x + headerLogoRect.width / 2,
        y: headerLogoRect.y + headerLogoRect.height / 2,
        size: 3 + Math.random() * 5,
        delay: 0,
      }));
      setBurstParticles(burst);
    }, FLIGHT_DURATION_MS * 0.78);

    // Cleanup after animation completes
    setTimeout(() => {
      setTransitionPhase('idle');
      setParticles([]);
      setBurstParticles([]);
      resetTransition();
      onFinishRef.current();
    }, FLIGHT_DURATION_MS + 500);
  }, [generateParticles]);

  // MAIN EFFECT: Initialize when visible becomes true
  useEffect(() => {
    if (!store.visible) {
      dismissing.current = false;
      minTimeElapsed.current = false;
      phaseInitialized.current = false;
      return;
    }

    dismissing.current = false;
    minTimeElapsed.current = false;
    setTextOpacity(1);
    setOverlayOpacity(1);
    setParticles([]);
    setBurstParticles([]);

    const centerX = (window.innerWidth - LOGO_SIZE) / 2;
    const centerY = (window.innerHeight - LOGO_SIZE) / 2;

    // Start at login logo position or center
    const startRect = store.loginLogoRect;
    if (startRect) {
      setLogoStyle({
        position: 'fixed',
        left: startRect.x,
        top: startRect.y,
        width: LOGO_SIZE,
        height: LOGO_SIZE,
        transform: `scale(${startRect.width / LOGO_SIZE})`,
        transformOrigin: 'top left',
        transition: 'none',
        zIndex: 1000,
      });
    } else {
      setLogoStyle({
        position: 'fixed',
        left: centerX,
        top: centerY,
        width: LOGO_SIZE,
        height: LOGO_SIZE,
        transform: 'scale(1)',
        transformOrigin: 'top left',
        transition: 'none',
        zIndex: 1000,
      });
    }

    // Small delay to ensure the initial position is painted, then animate to center
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (startRect) {
          setTransitionPhase('login-to-center');
          setLogoStyle({
            position: 'fixed',
            left: centerX,
            top: centerY,
            width: LOGO_SIZE,
            height: LOGO_SIZE,
            transform: 'scale(1)',
            transformOrigin: 'top left',
            transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 1000,
          });

          setTimeout(() => {
            setTransitionPhase('pulsating');
            phaseInitialized.current = true;
            setMessageOpacity(1);
          }, 600);
        } else {
          setTransitionPhase('pulsating');
          phaseInitialized.current = true;
          setMessageOpacity(1);
        }
      });
    });

    // Min display timer
    minTimerStartRef.current = Date.now();
    const minTimer = setTimeout(() => {
      minTimeElapsed.current = true;
    }, MIN_DISPLAY_MS);

    return () => {
      clearTimeout(minTimer);
      if (headerWaitTimerId.current) {
        clearTimeout(headerWaitTimerId.current);
        headerWaitTimerId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.visible]);

  // When settled + min time elapsed: attempt header transition or fade out
  useEffect(() => {
    if (!store.loginSettled || !store.visible) return;

    if (store.error) {
      dismiss();
      return;
    }

    const attemptTransition = () => {
      const { headerLogoRect } = getTransitionState();
      if (headerLogoRect && phaseInitialized.current) {
        startHeaderTransition();
      } else {
        headerWaitTimerId.current = setTimeout(() => {
          const { headerLogoRect: rect } = getTransitionState();
          if (rect && phaseInitialized.current) {
            startHeaderTransition();
          } else {
            dismiss();
          }
        }, HEADER_WAIT_TIMEOUT_MS);
      }
    };

    if (minTimeElapsed.current) {
      attemptTransition();
      return;
    }

    const elapsed = Date.now() - minTimerStartRef.current;
    const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);
    const waitTimer = setTimeout(attemptTransition, remaining);
    return () => clearTimeout(waitTimer);
  }, [
    store.loginSettled,
    store.visible,
    store.error,
    dismiss,
    startHeaderTransition,
  ]);

  // Watch for headerLogoRect during pulsating phase
  useEffect(() => {
    if (
      store.phase === 'pulsating' &&
      store.loginSettled &&
      !store.error &&
      store.headerLogoRect &&
      minTimeElapsed.current
    ) {
      if (headerWaitTimerId.current) {
        clearTimeout(headerWaitTimerId.current);
        headerWaitTimerId.current = null;
      }
      startHeaderTransition();
    }
  }, [
    store.headerLogoRect,
    store.phase,
    store.loginSettled,
    store.error,
    startHeaderTransition,
  ]);

  if (!store.visible) return null;

  const centerY = (window.innerHeight - LOGO_SIZE) / 2;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999,
        pointerEvents: 'none',
      }}
    >
      {/* Background overlay — matches login gradient */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(to bottom right, ${colors.background}, ${colors.offWhite})`,
          opacity: overlayOpacity,
          transition: 'opacity 0.5s ease',
        }}
      />

      {/* Trail particles — theme-colored dots along the arc */}
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position: 'fixed',
            left: p.x - p.size / 2,
            top: p.y - p.size / 2,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            backgroundColor: colors.primary,
            animation: `sparkle-trail 0.8s ease-out ${p.delay}ms both`,
            pointerEvents: 'none',
            zIndex: 1001,
          }}
        />
      ))}

      {/* Landing burst — circles radiating outward */}
      {burstParticles.map((p, i) => {
        const angle = (i / burstParticles.length) * 360;
        const distance = 25 + Math.random() * 20;
        return (
          <div
            key={`burst-${i}`}
            style={
              {
                position: 'fixed',
                left: p.x - p.size / 2,
                top: p.y - p.size / 2,
                width: p.size,
                height: p.size,
                borderRadius: '50%',
                backgroundColor: colors.primary,
                animation: 'landing-burst 0.6s ease-out both',
                '--burst-x': `${Math.cos((angle * Math.PI) / 180) * distance}px`,
                '--burst-y': `${Math.sin((angle * Math.PI) / 180) * distance}px`,
                pointerEvents: 'none',
                zIndex: 1001,
              } as React.CSSProperties
            }
          />
        );
      })}

      {/* Animated logo */}
      <div ref={logoElRef} style={logoStyle}>
        <PulsatingLogo
          size={LOGO_SIZE}
          animated={
            store.phase === 'pulsating' || store.phase === 'login-to-center'
          }
        />
      </div>

      {/* Loading messages */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          top: centerY + LOGO_SIZE + 24,
          display: 'flex',
          justifyContent: 'center',
          opacity: messageOpacity,
          transition: 'opacity 0.3s ease',
        }}
      >
        <p
          style={{
            color: colors.text.secondary,
            fontSize: typography.sizes.md,
            fontWeight: 400,
            opacity: textOpacity,
            transition: 'opacity 0.2s ease',
          }}
        >
          {LOADING_MESSAGES[messageIndex]}
        </p>
      </div>
    </div>
  );
}
